import { useCallback, useState } from 'react';

export interface State<Data> {
	data: Data | undefined;
	loading: boolean;
	error: Error | undefined;
}

/**
 * This is a copy of useService from @atlassian/jira-common-services with the following differences:
 * - The returned Promise from `fetch` will reject if there's an error. useService resolves to undefined
 * TODO: consider using fetchJson from @atlassian/help-center-common-util/fetch/fetch (FOR JSON)
 */
export const useService = <Data, RequestData = void>(
	request: (requestData: RequestData) => Promise<Data>,
	initialState: Partial<State<Data>> = {},
): State<Data> & {
	fetch: (requestData: RequestData) => Promise<Data>;
} => {
	const [state, setState] = useState<State<Data>>({
		loading: false,
		data: undefined,
		error: undefined,
		...initialState,
	});

	const fetch = useCallback(
		async (requestData: RequestData) => {
			setState({
				loading: true,
				error: undefined,
				data: undefined,
			});

			try {
				const data = await request(requestData);
				setState({
					data,
					loading: false,
					error: undefined,
				});
				return data;
			} catch (error) {
				setState({
					error: error as Error,
					loading: false,
					data: undefined,
				});
				return Promise.reject(error);
			}
		},
		[request],
	);

	return { ...state, fetch };
};
